/**
 * usePriceQuote: asks the BFF to price the current basket before an order
 * exists (server-priced checkout, spec 2026-09-23 quote-and-lock).
 *
 * Same keyed-and-debounced shape as `useQuoteEta` (the server prices, the
 * client only renders), plus `replace(q)` so a `PRICE_CHANGED` response from
 * order creation can update the displayed quote without a network round-trip.
 */
import { useEffect, useMemo, useState } from "react";

import { priceQuoteApi } from "@/api/services";
import type { PriceQuote, PriceQuoteRequest } from "@/api/online-ordering-service/model";

export type UsePriceQuoteOptions = {
  /** Milliseconds to wait after the last change before quoting. Default 400. */
  debounceMs?: number;
};

export type UsePriceQuoteResult = {
  quote: PriceQuote | null;
  loading: boolean;
  error: Error | null;
  /** Sets `quote` directly (e.g. from a 409 PRICE_CHANGED body) without a network call. */
  replace(q: PriceQuote): void;
};

const IDLE = { quote: null, loading: false, error: null } as const;

export function usePriceQuote(
  request: PriceQuoteRequest | null,
  opts: UsePriceQuoteOptions = {},
): UsePriceQuoteResult {
  const debounceMs = opts.debounceMs ?? 400;
  const key = useMemo(
    () => (request && request.branchId ? JSON.stringify(request) : ""),
    [request],
  );
  const [state, setState] = useState<{ quote: PriceQuote | null; loading: boolean; error: Error | null }>(IDLE);

  useEffect(() => {
    if (!key) {
      setState(IDLE);
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const timer = setTimeout(() => {
      priceQuoteApi
        .quote(JSON.parse(key) as PriceQuoteRequest)
        .then((quote) => {
          if (!cancelled) setState({ quote, loading: false, error: null });
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setState({ quote: null, loading: false, error: err instanceof Error ? err : new Error(String(err)) });
          }
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [key, debounceMs]);

  function replace(q: PriceQuote): void {
    setState({ quote: q, loading: false, error: null });
  }

  return { ...state, replace };
}
